/**
 * Lightweight in-memory image cache.
 *
 * Server banners (and other vault/user images) are loaded as base64 data URIs
 * or blob URLs and re-fetched every time the active vault changes, causing
 * a brief flash while the browser re-downloads. This cache preloads images
 * once via `new Image()` and keeps them alive in a Map keyed by URL so
 * switching vaults reads from the warm cache.
 *
 * Garbage-collection concern: the cache lives for the lifetime of the SPA
 * and never evicts. Server banners are typically a handful of small images,
 * so unbounded growth is not a real problem.
 */

const imageCache = new Map<string, string>();

/** Track in-flight loads to avoid duplicate requests. */
const inFlight = new Map<string, Promise<string | null>>();

/**
 * Preload a single image URL into the cache. Returns the cached URL
 * (which may be the same as the input if it's already a data: / blob: URI
 * that doesn't benefit from caching) or null if the load failed.
 */
export async function preloadImage(url: string): Promise<string | null> {
  if (!url) return null;

  // Already cached — immediate return.
  const cached = imageCache.get(url);
  if (cached) return cached;

  // Already being loaded — wait for the in-flight promise.
  const alreadyLoading = inFlight.get(url);
  if (alreadyLoading) return alreadyLoading;

  const promise = new Promise<string | null>((resolve) => {
    const img = new Image();
    img.onload = () => {
      imageCache.set(url, url);
      inFlight.delete(url);
      resolve(url);
    };
    img.onerror = () => {
      inFlight.delete(url);
      resolve(url); // return original URL so the browser can still try
    };
    img.src = url;
  });

  inFlight.set(url, promise);
  return promise;
}

/**
 * Preload a batch of image URLs. Non-blocking — fires requests and returns
 * immediately. Use when you know you'll need several images soon
 * (e.g. on initial vault list load).
 */
export function preloadImages(urls: string[]): void {
  for (const url of urls) {
    preloadImage(url);
  }
}

/**
 * Check whether an image URL is already cached (resolved).
 */
export function isImageCached(url: string): boolean {
  return imageCache.has(url);
}

/**
 * Get a cached URL synchronously. Returns the input URL if not yet cached
 * (the browser will still load it normally).
 */
export function getCachedUrl(url: string): string {
  return imageCache.get(url) ?? url;
}
